import { replaceState } from "@scripts/routes/router";
import { calculateNavOffset, getCurrentMainNavHeight } from "@scripts/util/navigation";

const scrollToElement = (element: Element, offset: number) => {
  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({
    top: targetPosition,
    behavior: "smooth",
  });
};

const handleAnchorClick = (defaultNavOffset: number) => (event: MouseEvent) => {
  if (!(event.target instanceof HTMLElement)) return;

  const anchor = event.target.closest("a");

  if (!anchor?.hash || anchor.hash === "#") return;

  // Check if the link is to the current page
  const url = new URL(anchor.href, window.location.href);
  if (url.pathname !== window.location.pathname) return;

  const targetId = anchor.hash.slice(1);
  const targetElement = document.getElementById(targetId);

  if (!targetElement) return;

  event.preventDefault();

  const mainNavHeight = getCurrentMainNavHeight(defaultNavOffset);
  const offset = calculateNavOffset(mainNavHeight);

  scrollToElement(targetElement, offset);

  replaceState(anchor.hash);
};

const handleInitialHash = (defaultNavOffset: number) => {
  if (!window.location.hash || window.location.hash === "#") return;

  const targetId = window.location.hash.slice(1);
  const targetElement = document.getElementById(targetId);

  if (!targetElement) return;

  // Small delay to ensure the page layout is complete
  globalThis.setTimeout(() => {
    const mainNavHeight = getCurrentMainNavHeight(defaultNavOffset);
    const offset = calculateNavOffset(mainNavHeight);
    scrollToElement(targetElement, offset);
  }, 100);
};

export const initializeAnchorScrolling = (defaultNavOffset: number = 60) => {
  // Handle clicks on anchor links
  document.addEventListener("click", handleAnchorClick(defaultNavOffset));

  // Handle initial page load with hash
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => handleInitialHash(defaultNavOffset));
  } else {
    handleInitialHash(defaultNavOffset);
  }

  // Handle browser back/forward navigation
  window.addEventListener("hashchange", () => handleInitialHash(defaultNavOffset));

  // Return cleanup function
  return () => {
    document.removeEventListener("click", handleAnchorClick(defaultNavOffset));
    document.removeEventListener("DOMContentLoaded", () => handleInitialHash(defaultNavOffset));
    window.removeEventListener("hashchange", () => handleInitialHash(defaultNavOffset));
  };
};
